import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { productsApi, type StockUpdateAttr } from '@/api/products'
import { cn } from '@/lib/utils'

interface StockUpdateDialogProps {
	productId: number | null
	onClose: () => void
}

export function StockUpdateDialog({ productId, onClose }: StockUpdateDialogProps) {
	const queryClient = useQueryClient()
	const { data: detail, isLoading } = useQuery({
		queryKey: ['product-stock', productId],
		queryFn: () => productsApi.getDetail(productId!),
		enabled: productId !== null,
		staleTime: 0,
	})
	// 每行：pm(1=增加/0=减少) + 输入数量
	const [editRows, setEditRows] = useState<Record<string, { pm: 0 | 1; stock: string }>>({})

	const updateMutation = useMutation({
		mutationFn: (attrs: StockUpdateAttr[]) => productsApi.updateStock(productId!, attrs),
		onSuccess: (data) => {
			toast.success(`库存修改成功，当前总库存: ${data.stock}`)
			queryClient.invalidateQueries({ queryKey: ['products'] })
			queryClient.invalidateQueries({ queryKey: ['product-detail', productId] })
			queryClient.invalidateQueries({ queryKey: ['product-stock', productId] })
			setEditRows({})
			onClose()
		},
		onError: () => { toast.error('库存修改失败') },
	})

	// 计算每行结果和汇总
	const { rows, totalDelta } = useMemo(() => {
		if (!detail) return { rows: [], totalDelta: 0 }
		let delta = 0
		const computed = detail.attr_value.map((sku) => {
			const edit = editRows[sku.unique] || { pm: 1 as 0 | 1, stock: '' }
			const amount = edit.stock ? Number(edit.stock) : 0
			const diff = edit.pm === 1 ? amount : -amount
			const resultStock = Math.max(sku.stock + diff, 0)
			delta += diff
			return { sku, edit, diff, resultStock }
		})
		return { rows: computed, totalDelta: delta }
	}, [detail, editRows])

	const handleSubmit = () => {
		const attrs: StockUpdateAttr[] = []
		for (const { sku, edit } of rows) {
			if (!edit.stock || Number(edit.stock) <= 0) continue
			attrs.push({ unique: sku.unique, pm: edit.pm, stock: Number(edit.stock) })
		}
		if (attrs.length === 0) { toast.warning('请至少填写一行有效的库存变更'); return }
		updateMutation.mutate(attrs)
	}

	const handleClose = () => { setEditRows({}); onClose() }

	return (
		<Dialog open={productId !== null} onOpenChange={(open) => !open && handleClose()}>
			<DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
				<DialogHeader><DialogTitle>修改库存</DialogTitle></DialogHeader>
				{isLoading && <div className="py-8 text-center text-muted-foreground">加载中...</div>}
				{detail && (
					<div className="space-y-3">
						<p className="text-sm text-muted-foreground">{detail.store_name}</p>
						<div className="border rounded-lg overflow-hidden">
							<table className="w-full text-sm">
								<thead className="bg-muted/50">
									<tr>
										<th className="text-left p-2">规格</th>
										<th className="text-right p-2">当前库存</th>
										<th className="text-center p-2">增减操作</th>
										<th className="text-right p-2">修改后</th>
									</tr>
								</thead>
								<tbody>
									{rows.map(({ sku, edit, diff, resultStock }) => {
										const hasInput = edit.stock !== '' && Number(edit.stock) > 0
										return (
											<tr key={sku.id} className="border-t">
												<td className="p-2">{sku.suk || '默认'}</td>
												<td className="text-right p-2 text-muted-foreground">{sku.stock}</td>
												<td className="p-2">
													<div className="flex justify-center items-center gap-0">
														<Input
															className="w-[70px] text-right rounded-r-none"
															type="number"
															min="0"
															placeholder="0"
															value={edit.stock ?? ''}
															onChange={(e) => setEditRows((prev) => ({ ...prev, [sku.unique]: { ...edit, stock: e.target.value } }))}
														/>
														<select
															className="h-[38px] rounded-r-md border border-l-0 bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
															value={edit.pm}
															onChange={(e) => setEditRows((prev) => ({ ...prev, [sku.unique]: { ...edit, pm: Number(e.target.value) as 0 | 1 } }))}
														>
															<option value={1}>增加</option>
															<option value={0}>减少</option>
														</select>
													</div>
												</td>
												<td className="text-right p-2">
													{hasInput ? (
														<span className={cn('font-medium', diff > 0 ? 'text-green-600' : 'text-red-600')}>
															{resultStock}
														</span>
													) : (
														<span className="text-muted-foreground">{sku.stock}</span>
													)}
												</td>
											</tr>
										)
									})}
								</tbody>
								<tfoot className="bg-muted/30 border-t">
									<tr>
										<td className="p-2 font-medium" colSpan={3}>库存总变化</td>
										<td className="text-right p-2">
											<span className={cn('font-semibold', totalDelta > 0 ? 'text-green-600' : totalDelta < 0 ? 'text-red-600' : 'text-muted-foreground')}>
												{totalDelta > 0 ? `+${totalDelta}` : totalDelta === 0 ? '-' : totalDelta}
											</span>
										</td>
									</tr>
								</tfoot>
							</table>
						</div>
					</div>
				)}
				<DialogFooter>
					<Button variant="outline" onClick={handleClose}>取消</Button>
					<Button onClick={handleSubmit} disabled={updateMutation.isPending}>
						{updateMutation.isPending ? '提交中...' : '保存修改'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
